import React, { useEffect } from 'react';
import { Table, Button, Tag, Space, message, Modal, Spin } from 'antd';
import { useDispatch, useSelector } from 'react-redux';
import { fetchLeaves, approveLeave, rejectLeave } from '../redux/slices/leaveSlice';
import { canApproveLeaves } from '../utils/permissions';

const LeaveTable = ({ showApprovalActions = false, showMyLeavesOnly = false }) => {
  const dispatch = useDispatch();
  const { leaves, pagination, isLoading } = useSelector(state => state.leaves);
  const { user } = useSelector((state) => state.auth);

  useEffect(() => {
    const filters = {};
    // For "My Leaves" tab, pass employeeId to filter
    // The backend will get the employee ID associated with the current user
    if (showMyLeavesOnly) {
      filters.myLeaves = 'true'; // Special flag to tell backend to filter by current user's employee
    }
    dispatch(fetchLeaves({ page: 1, limit: 10, filters }));
  }, [dispatch, showMyLeavesOnly]);

  const handleApprove = (id) => {
    Modal.confirm({
      title: 'Approve Leave',
      content: 'Are you sure you want to approve this leave request?',
      okText: 'Yes',
      cancelText: 'No',
      onOk() {
        dispatch(approveLeave({ id, comments: 'Approved' }))
          .then(() => message.success('Leave approved'))
          .catch(err => message.error(err || 'Failed to approve leave'));
      }
    });
  };

  const handleReject = (id) => {
    Modal.confirm({
      title: 'Reject Leave',
      content: 'Are you sure you want to reject this leave request?',
      okText: 'Yes',
      cancelText: 'No',
      onOk() {
        dispatch(rejectLeave({ id, comments: 'Rejected' }))
          .then(() => message.success('Leave rejected'))
          .catch(err => message.error(err || 'Failed to reject leave'));
      }
    });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Pending': return 'orange';
      case 'Approved': return 'green';
      case 'Rejected': return 'red';
      default: return 'blue';
    }
  };

  const columns = [
    {
      title: 'Employee',
      dataIndex: 'employee',
      key: 'employee',
      render: (_, record) => {
        const employee = record.employee;
        return employee ? `${employee.firstName} ${employee.lastName}` : '-';
      }
    },
    {
      title: 'Leave Type',
      dataIndex: 'leaveType',
      key: 'leaveType',
      render: (leaveType) => leaveType?.name || '-'
    },
    {
      title: 'From Date',
      dataIndex: 'startDate',
      key: 'startDate',
      render: (date) => new Date(date).toLocaleDateString()
    },
    {
      title: 'To Date',
      dataIndex: 'endDate',
      key: 'endDate',
      render: (date) => new Date(date).toLocaleDateString()
    },
    {
      title: 'Days',
      dataIndex: 'numberOfDays',
      key: 'days',
    },
    {
      title: 'Reason',
      dataIndex: 'reason',
      key: 'reason',
      render: (reason) => reason || '-',
      ellipsis: true
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status) => (
        <Tag color={getStatusColor(status)}>{status}</Tag>
      )
    },
    ...(showApprovalActions && canApproveLeaves(user) ? [{
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        record.status === 'Pending' && (
          <Space>
            <Button
              type="primary"
              size="small"
              onClick={() => handleApprove(record.id)}
            >
              Approve
            </Button>
            <Button
              danger
              size="small"
              onClick={() => handleReject(record.id)}
            >
              Reject
            </Button>
          </Space>
        )
      )
    }] : [])
  ];

  const handleTableChange = (paginationData) => {
    const filters = {};
    if (showMyLeavesOnly) {
      filters.myLeaves = 'true';
    }
    dispatch(fetchLeaves({
      page: paginationData.current,
      limit: paginationData.pageSize,
      filters
    }));
  };

  return (
    <Spin spinning={isLoading}>
      <Table
        columns={columns}
        dataSource={leaves}
        pagination={{
          current: pagination.page,
          pageSize: pagination.limit,
          total: pagination.total
        }}
        onChange={handleTableChange}
        rowKey="id"
      />
    </Spin>
  );
};

export default LeaveTable;
